import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, ChevronRight, Download, Edit, ExternalLink, Info, Plus, Trash2, Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppBadge, AppButton, AppCard, AppCardContent,
  Callout, ConfirmModal, EmptyState, SearchInput, SegmentedControl, Switch, UserAvatar, cn,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import TeamMemberModal from './TeamMemberModal';
import ImportInstructorsModal from './ImportInstructorsModal';
import {
  TEAM_KIND_LABEL, isMentor, isTrainer, teamKindOf,
} from './teamKinds';
import {
  deleteTeamMember, listTeamMembers, reorderTeamMembers, updateTeamMember,
} from '../../utils/cmsAdminApi';

// ─── Website → Team — the trainers and mentors shown on the website ──────
//
// A profile here is EDITORIAL. It has no login, no instructor seat and no
// presence anywhere in the LMS — which is the whole reason the tab exists: an
// institute can put a visiting faculty member or an industry mentor on its
// website without giving them access to its students' data. That promise is
// stated at the top of the tab rather than left to be inferred, because the
// screen looks exactly like the one that DOES create accounts.
//
// Each profile is a trainer, a mentor or both (./teamKinds). The website shows
// them all in ONE "Trainers & mentors" section on the About page, and on the
// home page when the institute switches it on there (Pages → Home).
//
// Each card saves on its own, as blog posts and testimonials do: a profile is
// its own record, so the page's shared save bar has nothing to do with it.
//
// Props:
//   onGo          — ({ tab }) navigate to another Website tab
//   pageUrl       — (path) → the public URL, for "View on website"
//   notify        — (message, type) toast
//   headingTarget — where the About section's heading is edited, or null when
//                   the active layout does not expose it
//   homeTarget    — where the home page's section is switched on, or null

const SKILLS_ON_CARD = 4;

const VIEWS = [
  { value: 'all', label: 'All' },
  { value: 'trainers', label: 'Trainers' },
  { value: 'mentors', label: 'Mentors' },
];

const TeamTab = ({
  onGo, pageUrl, notify, headingTarget = null, homeTarget = null,
}) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all');
  const [editing, setEditing] = useState(null);     // null = closed, {} = new
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState('');
  // A card whose switch was just flipped is shown in its new state at once and
  // rolled back only if the write fails — a visibility toggle that waits for a
  // round trip reads as a dead control.
  const reorderSeq = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTeamMembers();
      setMembers(res.data || []);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleCount = members.filter((m) => m.isActive !== false).length;

  const counts = useMemo(() => ({
    all: members.length,
    trainers: members.filter(isTrainer).length,
    mentors: members.filter(isMentor).length,
  }), [members]);
  // The view filter earns its place once the list mixes the two.
  const mixed = new Set(members.map(teamKindOf)).size > 1;
  const activeView = mixed ? view : 'all';

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inView = members.filter((m) => (activeView === 'trainers' ? isTrainer(m)
      : activeView === 'mentors' ? isMentor(m) : true));
    if (!q) return inView;
    return inView.filter((m) => [m.name, m.designation, ...(m.skills || [])]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(q)));
  }, [members, search, activeView]);

  // Order is a property of the whole list, so it cannot be edited through a
  // filtered view — the arrows would move a profile past rows that are not on
  // screen. They are hidden while a search or a view is on, and the reason is said.
  const canReorder = !search.trim() && activeView === 'all';

  const saved = (message) => { notify(message); load(); };

  const toggleVisible = async (member) => {
    const next = member.isActive === false;
    setMembers((rows) => rows.map((m) => (m._id === member._id ? { ...m, isActive: next } : m)));
    setBusyId(member._id);
    try {
      await updateTeamMember(member._id, { isActive: next });
      notify(next ? `${member.name} is now shown on your website` : `${member.name} is hidden from your website`);
    } catch (err) {
      setMembers((rows) => rows.map((m) => (m._id === member._id ? { ...m, isActive: !next } : m)));
      notify(err?.response?.data?.message || 'Could not change this profile', 'error');
    } finally {
      setBusyId('');
    }
  };

  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= members.length) return;
    const next = [...members];
    [next[index], next[target]] = [next[target], next[index]];
    setMembers(next);
    // Each move sends the whole order; only the latest one's failure is worth
    // reporting, so a quick run of clicks does not stack up toasts.
    const seq = ++reorderSeq.current;
    try {
      await reorderTeamMembers(next.map((m) => m._id));
    } catch (err) {
      if (seq === reorderSeq.current) {
        notify(err?.response?.data?.message || 'Could not save the new order', 'error');
        load();
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTeamMember(deleting._id);
      setDeleting(null);
      saved(`${deleting.name} was removed from your website`);
    } catch (err) {
      notify(err?.response?.data?.message || 'Could not delete this profile', 'error');
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Callout tone="info" icon={<Info size={14} aria-hidden="true" />}>
        <div className="tw-flex tw-flex-col tw-gap-1">
          <strong className="tw-text-sm">These profiles are for your website only.</strong>
          <span className="tw-text-sm">
            Adding someone here does not create a login and does not use up an instructor
            on your plan — it only puts their photo and details on your public pages. To give
            someone access to the LMS, add them under{' '}
            <Link to="/instructors" className="tw-font-medium tw-text-primary tw-underline">
              Instructors
            </Link>{' '}
            instead. You can do both for the same person.
          </span>
        </div>
      </Callout>

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-flex tw-min-w-0 tw-flex-col">
              <span className="tw-text-sm tw-font-semibold tw-text-content">
                Trainers &amp; mentors
              </span>
              <span className="tw-text-xs tw-text-content-subtle">
                {loading
                  ? 'Loading…'
                  : members.length === 0
                    ? 'Shown in the Trainers & mentors section of your About page — and your home page, if you switch it on there — and on any course page you link them to.'
                    : `${visibleCount} of ${members.length} shown on your website, in the order below.`}
              </span>
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {members.length > 0 && (
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search name, role or skill…"
                  className="tw-w-full sm:tw-w-56"
                />
              )}
              <AppButton size="sm" variant="outline" onClick={() => setImporting(true)}>
                <Download size={16} /> Import from instructors
              </AppButton>
              <AppButton size="sm" onClick={() => setEditing({})}>
                <Plus size={16} /> Add profile
              </AppButton>
            </div>
          </div>

          {loading && <Shimmer height={120} count={3} />}

          {!loading && loadFailed && (
            <Callout tone="danger" role="alert">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <span>These profiles could not be loaded.</span>
                <AppButton size="sm" variant="outline" onClick={load}>Try again</AppButton>
              </div>
            </Callout>
          )}

          {!loading && !loadFailed && members.length === 0 && (
            <EmptyState
              icon={<Users size={40} />}
              title="No profiles yet"
              description="Add the trainers who teach your courses and the mentors who guide your students. Already have instructors in the LMS? Import their details in one step and edit the copy afterwards."
              action={(
                <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
                  <AppButton onClick={() => setEditing({})}>
                    <Plus size={16} /> Add profile
                  </AppButton>
                  <AppButton variant="outline" onClick={() => setImporting(true)}>
                    <Download size={16} /> Import from instructors
                  </AppButton>
                </div>
              )}
            />
          )}

          {!loading && !loadFailed && mixed && (
            <SegmentedControl
              label="Show profiles"
              value={activeView}
              onChange={setView}
              options={VIEWS.map((v) => ({ ...v, count: counts[v.value] }))}
            />
          )}

          {!loading && !loadFailed && members.length > 0 && shown.length === 0 && (
            <p className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-content-subtle">
              {search.trim()
                ? `No ${activeView === 'all' ? 'profile' : activeView.slice(0, -1)} matches “${search.trim()}”.`
                : `No ${activeView} yet.`}
            </p>
          )}

          {!loading && shown.length > 0 && (
            <>
              {!canReorder && (
                <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
                  {activeView === 'all'
                    ? 'Clear the search to change the order.'
                    : 'Show everyone to change the order — it is one order for the whole section.'}
                </p>
              )}
              <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-3 tw-p-0 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                {shown.map((member) => {
                  const index = members.findIndex((m) => m._id === member._id);
                  const hidden = member.isActive === false;
                  const skills = member.skills || [];
                  return (
                    <li
                      key={member._id}
                      className={cn(
                        'tw-relative tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid',
                        'tw-border-line tw-bg-surface tw-p-4',
                        hidden && 'tw-bg-surface-alt',
                      )}
                    >
                      <div className="tw-flex tw-items-start tw-gap-3">
                        <UserAvatar name={member.name} src={member.photoUrl || undefined} size="lg" bordered />
                        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                          {/* The name opens the profile, and its ::after covers
                              the card — the card is a summary, the dialog has
                              the whole profile. A real button, stretched, not a
                              click handler on the <li>: the card has to be
                              reachable by keyboard and announced as a control. */}
                          <button
                            type="button"
                            onClick={() => setEditing(member)}
                            className={cn(
                              'tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left',
                              'tw-text-sm tw-font-semibold tw-text-content tw-focus-ring',
                              "after:tw-absolute after:tw-inset-0 after:tw-content-['']",
                            )}
                          >
                            {member.name}
                          </button>
                          {member.designation && (
                            <span className="tw-truncate tw-text-xs tw-text-content-subtle">
                              {member.designation}
                            </span>
                          )}
                          {member.experience && (
                            <span className="tw-mt-0.5 tw-text-xs tw-text-content-subtle">
                              {member.experience} experience
                            </span>
                          )}
                          <span className="tw-mt-1.5">
                            <AppBadge size="sm" variant={teamKindOf(member) === 'trainer' ? 'outline' : 'secondary'}>
                              {TEAM_KIND_LABEL[teamKindOf(member)]}
                            </AppBadge>
                          </span>
                        </div>
                        <span className="tw-relative tw-z-10">
                          <Switch
                            checked={!hidden}
                            disabled={busyId === member._id}
                            onChange={() => toggleVisible(member)}
                            aria-label={`Show ${member.name} on the website`}
                          />
                        </span>
                      </div>

                      {skills.length > 0 && (
                        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1 tw-p-0">
                          {skills.slice(0, SKILLS_ON_CARD).map((skill) => (
                            <li
                              key={skill}
                              className="tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-xs tw-text-primary"
                            >
                              {skill}
                            </li>
                          ))}
                          {skills.length > SKILLS_ON_CARD && (
                            <li className="tw-px-1 tw-py-0.5 tw-text-xs tw-text-content-subtle">
                              +{skills.length - SKILLS_ON_CARD} more
                            </li>
                          )}
                        </ul>
                      )}

                      {member.bio && (
                        <p className="tw-m-0 tw-line-clamp-2 tw-text-xs tw-text-content-subtle">
                          {member.bio}
                        </p>
                      )}

                      <div className="tw-mt-auto tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <span className="tw-text-xs tw-text-content-subtle">
                          {hidden ? 'Hidden from your website' : `Position ${index + 1}`}
                        </span>
                        <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-gap-1">
                          <AppButton size="sm" variant="outline" onClick={() => setEditing(member)}>
                            <Edit size={14} /> Edit
                          </AppButton>
                          <ActionMenu label={`Actions for ${member.name}`}>
                            {canReorder && (
                              <>
                                <ActionMenuItem onClick={() => move(index, -1)} disabled={index === 0}>
                                  <ArrowUp size={14} /> Move up
                                </ActionMenuItem>
                                <ActionMenuItem
                                  onClick={() => move(index, 1)}
                                  disabled={index === members.length - 1}
                                >
                                  <ArrowDown size={14} /> Move down
                                </ActionMenuItem>
                                <ActionMenuDivider />
                              </>
                            )}
                            <ActionMenuItem danger onClick={() => setDeleting(member)}>
                              <Trash2 size={14} /> Delete
                            </ActionMenuItem>
                          </ActionMenu>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {!loading && members.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-3">
              <AppButton
                as="a"
                size="sm"
                variant="ghost"
                href={pageUrl('/about#team')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={14} /> View on your website
              </AppButton>
              {headingTarget && (
                <AppButton size="sm" variant="ghost" onClick={() => onGo(headingTarget)}>
                  Edit the About page heading <ChevronRight size={14} />
                </AppButton>
              )}
              {homeTarget && (
                <AppButton size="sm" variant="ghost" onClick={() => onGo(homeTarget)}>
                  Show them on your home page <ChevronRight size={14} />
                </AppButton>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <TeamMemberModal
        member={editing}
        onClose={() => setEditing(null)}
        onSaved={(message) => { setEditing(null); saved(message); }}
        notify={notify}
      />

      <ImportInstructorsModal
        show={importing}
        onClose={() => setImporting(false)}
        onImported={(message) => { setImporting(false); saved(message); }}
        notify={notify}
      />

      <ConfirmModal
        show={!!deleting}
        title="Delete this profile?"
        message={deleting
          ? `${deleting.name} will be removed from your website. Their LMS account, if they have one, is not affected.`
          : ''}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
};

export default TeamTab;
